import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';

import { getStatusPresentation, ATTENDANCE_DOMAIN } from 'utils/StatusPresentation';

/**
 * Competition Design System — the ONE status badge for competitor / match
 * states. Never create page-specific status labels: a "Walkover" chip must
 * look identical on the bracket, the schedule, live results and the PDF.
 *
 * The status KEY is stable and machine-readable (data-status); the human
 * label is localized under `competition.status.<key>` with an English
 * fallback. New states extend {@link STATUSES}/{@link FALLBACKS} without
 * breaking callers.
 *
 * Colors are NEVER literal hex here — every key resolves to a `tone`
 * ('success'|'warning'|'caution'|'error'|'info'|'neutral'), and `tone` is
 * looked up against `theme.palette` (front/src/theme/palette.js) inside
 * `useStyles`, the one place in this file with theme access. The five keys
 * that correspond to a real CompetitionAttendanceStatus value (present/
 * absent/withdrawn/disqualified/medicalWithdrawal) get their tone from the
 * Status Presentation System's registered 'attendance' domain
 * (front/src/utils/StatusPresentation/AttendanceStatusTheme.js) — that
 * registry is the single source of truth for what those five statuses mean
 * presentationally; this file never re-decides it. The remaining keys are
 * match-outcome/workflow annotations with no backend enum behind them (not
 * a registered "domain"), so they keep their own small local tone table.
 */
const BADGE_KEY_TO_ATTENDANCE_STATUS = {
    present: 'PRESENT',
    absent: 'ABSENT',
    withdrawn: 'WITHDRAWN',
    disqualified: 'DISQUALIFIED',
    medicalWithdrawal: 'MEDICAL_WITHDRAWAL',
};

const MATCH_OUTCOME_TONES = {
    winner: 'success',
    loser: 'neutral',
    walkover: 'info',
    autoAdvance: 'info',
    dns: 'caution',
    locked: 'neutral',
    split: 'info',
};

const FALLBACKS = {
    winner: 'Winner',
    loser: 'Loser',
    walkover: 'Walkover',
    autoAdvance: 'Auto advance',
    disqualified: 'Disqualified',
    dns: 'DNS',
    withdrawn: 'Withdrawn',
    locked: 'Locked',
    split: 'Split',
    present: 'Present',
    absent: 'Absent',
    medicalWithdrawal: 'Medical withdrawal',
};

const attendancePresentationOf = status => {
    const attendanceStatus = BADGE_KEY_TO_ATTENDANCE_STATUS[status];
    return attendanceStatus ? getStatusPresentation(ATTENDANCE_DOMAIN, attendanceStatus) : null;
};

const toneOf = status => {
    const presentation = attendancePresentationOf(status);
    if (presentation) {
        return presentation.tone;
    }
    return MATCH_OUTCOME_TONES[status] || 'neutral';
};

/**
 * The icon a non-badge consumer (AthleteStatusLabel) pairs with the badge —
 * only attendance statuses carry one today (registered in
 * AttendanceStatusTheme.js); match-outcome keys have none. Exported so the
 * badgeKey→CompetitionAttendanceStatus lookup exists in exactly one place.
 */
export const getStatusIcon = status => {
    const presentation = attendancePresentationOf(status);
    return presentation ? presentation.icon : null;
};

/**
 * The same English fallback StatusBadge itself renders — exported so any
 * other consumer (AthleteStatusLabel's tooltip) shows identical text when a
 * translation key is missing, instead of re-typing its own fallback.
 */
export const getStatusFallback = status => FALLBACKS[status] || status;

// Truthy-map (not a color table) — the surviving public contract callers
// (CompetitorCard's `Object.keys(STATUSES)` / `STATUSES[key]` check) need.
export const STATUSES = Object.keys(FALLBACKS).reduce((all, key) => {
    all[key] = true;
    return all;
}, {});

const SIZE = {
    sm: { font: 9, padV: 1, padH: 5 },
    md: { font: 11, padV: 2, padH: 8 },
};

const useStyles = makeStyles(theme => ({
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 3,
        fontWeight: 700,
        letterSpacing: 0.4,
        lineHeight: 1,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        userSelect: 'none',
    },
    // One class per tone — the ONLY place any of these colors is written.
    success: { color: theme.palette.success.dark, backgroundColor: theme.palette.success.light },
    warning: { color: theme.palette.warning.dark, backgroundColor: theme.palette.warning.light },
    caution: { color: theme.palette.caution.dark, backgroundColor: theme.palette.caution.light },
    error: { color: theme.palette.error.dark, backgroundColor: theme.palette.error.light },
    info: { color: theme.palette.info.dark, backgroundColor: theme.palette.info.light },
    neutral: { color: theme.palette.neutral.dark, backgroundColor: theme.palette.neutral.light },
}));

const StatusBadge = props => {
    const { status, size, className } = props;
    const classes = useStyles();
    const [t] = useTranslation();

    if (!STATUSES[status]) {
        return null;
    }
    const dims = SIZE[size] || SIZE.md;
    const toneClass = classes[toneOf(status)] || classes.neutral;

    return (
        <span
            className={[classes.badge, toneClass, className].filter(Boolean).join(' ')}
            style={{
                fontSize: dims.font,
                padding: `${dims.padV}px ${dims.padH}px`,
            }}
            data-testid='status-badge'
            data-status={status}
        >
            {t(`competition.status.${status}`, FALLBACKS[status])}
        </span>
    );
};

StatusBadge.propTypes = {
    // Intentionally a plain string: unknown statuses are forward-compatible
    // (the badge renders nothing), so a strict oneOf would warn by design.
    status: PropTypes.string.isRequired,
    size: PropTypes.oneOf(['sm', 'md']),
    className: PropTypes.string,
};

StatusBadge.defaultProps = {
    size: 'md',
    className: null,
};

export default StatusBadge;
